import * as _ from 'lodash'
import { Div, Section, Article, ElementProperties, VArray, VString, Span, Button, A, P, all } from 'alkali'
import { selectedTreatments, selectedOutcomes, addOutcome, selectedCharacteristics, cumulativeAllOutcomes } from 'model/EvidenceSearch'
import StudySummaryModel from './model/StudySummaryModel'
import ExtractionModel from 'components/extract/model/ExtractionModel'
import type { DAStudyGroup, DAStudyOutcome, REFullStudy } from 'services/extract/types'
import { liveParsed, analysisType } from 'model/SearchQuery'
import './ExtractPane.less'
import AnnotateAbstract from './AnnotateAbstract'
import EditAbstract from './EditAbstract'
import ExtractAnnotationModel from './ExtractAnnotationModel'
import ExtractCol from './ExtractCol'
import DetailsCol from './DetailsCol'
import ReadOnlyAbstract from 'components/extract/ReadOnlyAbstract'
import VAbstractSource, { VUploadAbstractSource } from 'components/extract/model/AbstractSource'
import { post } from 'tools/request'
import { initializeQuantifierParser } from 'components/extract/model/Scanner'
import StudyContext from 'components/extract/model/StudyContext'
import { ScreeningPane } from 'components/screening/ScreeningPane'
import LoadingEllipsis from 'components/generic/LoadingEllipsis'
import Icon from 'components/Icon'
import * as UploadedIcon from 'icons/arrow-thick-circle-up-2.svg'
import * as OpenBookIcon from 'icons/book-open-text.svg'
import app from 'app/app'
import type { ArticleMeta } from 'types/portal'
import Dialog from '../generic/DialogAlkali'

export interface ExtractPaneOpts extends ElementProperties<ExtractPane> {
	mode: string
	summary: StudySummaryModel
	selectedOutcome: DAStudyOutcome
	selectedGroups: DAStudyGroup[]
	onSubmit?: () => void
	readonly?: boolean
	manage?: boolean
	moveToNext: any
	studyList: any
	debug?: boolean
}

const DELETE_WARNING = 'Deleting a study is final. Do you still wish to delete this study?'

export const DeleteButton = (study, callback?) =>
	Button('.delete-study.clickable', {
		onclick() {
			if (confirm(DELETE_WARNING)) {
				post('/portal/study/' + study.id + '/archive').then(() => callback?.())
			}
		}
	}, ['Delete Study'])
class OutcomeDialog extends Dialog('#extract-outcome-dialog') {
		doSubmit: any;

		get allowBackdropDismiss() {
			return false
		}

		get defaults() {
			return {
				head: {
					titleText: 'Extraction Follow-Up',
					close: true,
				},
				foot: {},
			}
		}
		onHide() {
			this.doSubmit()
		}

		ready(properties) {
			const {newOutcomes, onSubmit} = properties
			
			//set function for onHide use
			this.doSubmit = onSubmit
			
			// Need to grab the current outcome(s)
			const questionOutcomes = selectedOutcomes.valueOf()
			this.body.append(
				Div([
					P('Your extraction has been successfully submitted!'),
					P(['We noticed that the outcome you extracted ( ', Span('.strong', [newOutcomes[0].label]), ' ) and the outcome in your question ( ', Span('.strong', [questionOutcomes[0].label]),' ) do not match.']),
					P('Would you like to update your question to match the extracted outcome?')
				])
			)
			this.footer.leftControls.append(
				Button('.secondary', {
					textContent: 'Don\'t Update Question',

					onclick: () => {
						this.hide()
					},
				}),
			)
			this.footer.rightControls.append(

				Button('.primary#add-category', {
				textContent: 'Update Question',
				onclick: () => {
					addOutcome(newOutcomes[0])
					this.hide()
				},
			}))
		}
}

/**
 * Abstraction of the content of a containing extraction dialog or pane, generating the header and pane
 */
export class StudyDetails {
	readonly abstractSource
	readonly canDelete: boolean
	constructor(private readonly study, private readonly user) {
		this.abstractSource = study ? new VAbstractSource() : VUploadAbstractSource.create(app.user)
		this.canDelete = study?.metadata?.created && study?.id && study?.id != study?.articleId &&
			((study.metadata?.IsUploaded && (app.isSuperAdmin() || study?.metadata?.Extractor?.User?.Id == user.Id)) ||
				study.metadata?.IsAutomated && app.isSuperAdmin())
	}

	/** Generates a header including title and study source buttons */
	createStudyTitle(annotate?) {
		const year = this.study?.year
		const studyTitle = this.study ? new VString((year ? (year + ' - ') : '') + this.study.name) : this.abstractSource.property('Title').to(t => t || 'New Study')
		return Span([
			studyTitle,
			this.study?.metadata?.IsUploaded ? Icon('.uploaded-icon', { title: 'Manually uploaded', icon: UploadedIcon }) : undefined,
		])
	}

	private fetchArticleMeta(article): Promise<ArticleMeta | void> {
		return post<ArticleMeta>('/portal/articlemeta/search', { article }).catch(e => {
			if (e.status != 404) throw e
		})
	}

	private fetchFullStudy(study): Promise<REFullStudy | void> {
		const params = study.metadata.created ? { study: study.id } : { article: study.articleId }
		return post<REFullStudy>('/portal/fullstudy/search', params).catch(e => {
			if (e.status != 404) throw e
		})
	}

	/**
	 * Generates the top-level extract pane content. In addition to the specified arguments, the top-level inputs
	 * include the global selectedCharacteristics, selectedOutcomes, selectedTreatments, parsed query annotations,
	 * and aggregate outcomes from DA cooccurrences
	 * @param mode extract pane mode: screening or extract
	 * @param groups groups selected in study list
	 * @param outcome outcome selected in study list
	 * @param annotate used to indicate whether we are in a read-only mode
	 * @param manage whether to expose the group/outcome management tabs on study tab
	 * @param debug
	 * @param onSubmit action to take on extraction submission (e.g. clearing the search cache and reloading the page)
	 * @param studyList given to the ScreeningPane in screening mode
	 * @param moveToNext given to the ScreeningPane in screening mode
	 */
	createExtractPane(mode, { groups, outcome, annotate, manage, debug, onSubmit, studyList = undefined, moveToNext = undefined}) {
		const fullStudyData = Promise.all([
			initializeQuantifierParser(),
			((this.study && !this.study.metadata?.created) ? this.fetchArticleMeta(this.study.articleId) : undefined),
			this.study && this.fetchFullStudy(this.study)
		]).then(([_ignored, articleMeta, fullStudy]) => {
			return [articleMeta, fullStudy, this.study]
		})

		const _onSubmit = newOutcomes => {
			if (this.study && newOutcomes?.length && liveParsed.annotations.valueOf().length) {
				new OutcomeDialog({newOutcomes, onSubmit}).show(event)
			} else {
				onSubmit?.()
			}
		}

		return all([fullStudyData, mode], ([articleMeta, fullStudy, study], mode) => {
			const readonly = annotate == false
			//const onSubmit = readonly ? undefined : () => this.onSubmit()
			const studyCtx = new StudyContext(
				fullStudy,
				app.user,
				selectedCharacteristics.valueOf(),
				selectedOutcomes.valueOf(),
				selectedTreatments.valueOf(),
				liveParsed.annotations,
				Array.from(cumulativeAllOutcomes.map.values())
			)
			const summary = new StudySummaryModel(this.abstractSource, study, articleMeta, studyCtx, _onSubmit)
			return new ExtractPane({
				summary,
				mode,
				selectedOutcome: outcome,
				selectedGroups: groups,
				onSubmit: _onSubmit,
				studyList,
				moveToNext,
				readonly,
				manage,
				debug
			})
		}).whileResolving(new LoadingEllipsis())
	}
}

/** Top-level extract pane container */
class ExtractPane extends Div('.extract-content') {
	whenLoaded: Promise<void>
	created(props: ExtractPaneOpts) {
		const { mode, manage, summary, selectedOutcome, selectedGroups, onSubmit, readonly, debug, studyList, moveToNext } = props
		const { study, abstractSource, abstract, editingAbstract, allToggle } = summary
		const extractionModel = new ExtractionModel(
			summary,
			analysisType.valueOf(),
			selectedOutcome,
			selectedGroups,
			onSubmit,
			mode == 'screening' // show only the DS PICO annotations, not parsed numeric/measurement annotations
		)
		this.whenLoaded = extractionModel.whenLoaded
		const { annotatedAbstract } = extractionModel
		const annotModel = new ExtractAnnotationModel(abstract, annotatedAbstract)
		const hasDetails = summary.studyCtx.extractions?.length || !_.isEmpty(summary.studyCtx.study?.Sections)
		const showDetails = hasDetails || manage

		const articleCol = readonly ?
			Article('.abstract-col', {
				classes: {
					fullwidth: !showDetails
				}
			}, [
				new ReadOnlyAbstract({ summary })
			]) :
			Section('.abstract-col', editingAbstract.to(e => [
				// if we're editing show the editable abstract
				e ? new EditAbstract({
					abstractSource,
					edit: editingAbstract,
					debug,
					allToggle
				// otherwise the interactive annotated abstract
				}) : new AnnotateAbstract({
					study,
					model: annotModel,
					abstractSource,
					edit: editingAbstract,
					debug,
					allToggle
				})
			]))

		let detailsCol
		if (mode == 'screening') {
			detailsCol = new ScreeningPane({
				study,
				studyList,
				moveToNext,
				annotModel
			})
		} else if (mode == 'extract') {
			detailsCol = !readonly && manage ? new ExtractCol({ extractionModel, annotModel, debug, study, onSubmit })
							: (showDetails ? new DetailsCol({ extractionModel, debug, manage }) : undefined)
		} else {
			detailsCol = new ScreeningPane({
				study,
				studyList,
				moveToNext,
				annotModel
			})
		}

		props.content = [
			articleCol,
			detailsCol
		]
	}

}

export default ExtractPane
